import { Button } from '@creditornot/cb-button';
import { FormApi, ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import * as R from 'ramda';
import React, { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { PickingArea } from 'apis/picker-api/types';
import { ProductBrand } from 'apis/product-information-catalog/types';
import { Category, MenuConfigs, ProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { VatCategory } from 'apis/vat-categories/types';
import validateYupSchema from 'common/utils/validateYupSchema';
import MenuEditorConfirmationPrompt from 'modules/menu/components/MenuEditorConfirmationPrompt';
import { transformApiFieldNamesToFormFieldNames } from 'modules/menu/items/transformers';
import { ItemQuantity } from 'modules/menu/items/types';

import StickyFooter from './StickyFooter';
import { WOLT_SPECIALS_CATEGORY_ID } from '../../../constants';
import { FormValues, ItemDetailsFormFields, ValidationContext, validationSchema } from '../../ItemDetailsForm';
import { CommissionConfirmationModal } from '../ItemCommissions/CommissionConfirmationModal';

export type Props = {
  categories: Category[];
  vatCategories: VatCategory[] | undefined;
  className?: string;
  currency: Currency;
  initialValues: FormValues;
  productInfoSettings: ProductInformationSettings | undefined;
  itemQuantity: ItemQuantity | undefined;
  pickingAreas: PickingArea[] | undefined;
  brand: ProductBrand | null;
  syncedFieldNames: NonNullable<MenuConfigs['source_config']>['synced_field_names'];
  returnPolicies: MenuConfigs['item_return_policies'];
  loadBrandOptions: (search: string) => Promise<{ label: string; value: string }[]>;
  onCancel: () => void;
  onSubmit: (
    formValues: FormValues,
    nutritionFactsFormat: 'table' | 'text',
    form?: FormApi<FormValues>,
  ) => Promise<ValidationErrors | void>;
};

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'secondary' })`
  margin-inline-end: 16px;
`;

const SaveButton = styled(Button).attrs({ size: 'small', variant: 'primary' })``;

const ItemDetailsEditForm = ({
  categories,
  vatCategories,
  className,
  currency,
  initialValues,
  productInfoSettings,
  pickingAreas,
  itemQuantity,
  brand,
  loadBrandOptions,
  onCancel,
  onSubmit,
  syncedFieldNames,
  returnPolicies,
}: Props) => {
  const [isCommissionConfirmationModalOpen, setCommissionConfirmationModalOpen] = useState(false);

  const [nutritionFactsFormat, setNutritionFactsFormat] = useState<'text' | 'table'>(
    initialValues.nutritionValues.some(n => n.value !== undefined) ? 'table' : 'text',
  );

  const validationContext = useMemo<ValidationContext>(() => ({ currency }), [currency]);

  const syncedFeedFields = useMemo(() => transformApiFieldNamesToFormFieldNames(syncedFieldNames), [syncedFieldNames]);

  const commissionsProperties: (keyof FormValues)[] = [
    'homedeliveryRelativeCommission',
    'takeawayRelativeCommission',
    'eatinRelativeCommission',
  ];

  const haveCommissionsChanged = (previousValues: FormValues, currentValues: FormValues) =>
    !R.equals(R.pick(commissionsProperties, previousValues), R.pick(commissionsProperties, currentValues));

  const handleConfirmSubmission =
    (values: FormValues, submitCb: () => void) => (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (haveCommissionsChanged(initialValues, values)) {
        setCommissionConfirmationModalOpen(true);
      } else {
        submitCb();
      }
    };
  return (
    <Form<FormValues>
      initialValues={initialValues}
      onSubmit={(formValues: FormValues, form: FormApi<FormValues>) => onSubmit(formValues, nutritionFactsFormat, form)}
      mutators={{ ...arrayMutators }}
      validate={validateYupSchema(validationSchema, validationContext)}
    >
      {({ handleSubmit, hasValidationErrors, pristine, submitting, values }) => (
        <>
          <CommissionConfirmationModal
            contentName={initialValues.name}
            isOpen={isCommissionConfirmationModalOpen}
            onClose={() => setCommissionConfirmationModalOpen(false)}
            onConfirm={() => {
              setCommissionConfirmationModalOpen(false);
              handleSubmit();
            }}
          />

          <form className={className} onSubmit={handleConfirmSubmission(values, handleSubmit)}>
            <ItemDetailsFormFields
              allowCategoryChange={initialValues.categoryId !== WOLT_SPECIALS_CATEGORY_ID}
              // Allow removing user_requirements on all items, but only allow adding on Specials.
              allowUserRequirements={
                initialValues.categoryId === WOLT_SPECIALS_CATEGORY_ID || !!initialValues.userRequirement
              }
              // Allow setting items as Wolt+ exclusives for Wolt Specials only, but allow removing it for all items
              enableWoltPlusToggle={
                initialValues.categoryId === WOLT_SPECIALS_CATEGORY_ID || !!initialValues.isWoltPlusOnly
              }
              categories={categories}
              vatCategories={vatCategories}
              currency={currency}
              syncedFeedFields={syncedFeedFields}
              itemQuantity={itemQuantity}
              mode="edit"
              onChangeNutritionFactsFormat={setNutritionFactsFormat}
              nutritionFactsFormat={nutritionFactsFormat}
              productInfoSettings={productInfoSettings}
              pickingAreas={pickingAreas}
              returnPolicies={returnPolicies}
              brand={brand}
              loadBrandOptions={loadBrandOptions}
            />
            <StickyFooter>
              <CancelButton onClick={onCancel} type="button">
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SaveButton disabled={pristine || hasValidationErrors} loading={submitting} type="submit">
                <FormattedMessage id="state.save" />
              </SaveButton>
            </StickyFooter>
            <MenuEditorConfirmationPrompt when={!pristine} />
          </form>
        </>
      )}
    </Form>
  );
};

export default ItemDetailsEditForm;
